import { FC } from 'react';
import { Icon, IconName } from '@tuic/web-compositions/core/Icon';
import {
  Pictogram,
  PictogramName,
} from '@tuic/web-compositions/core/Pictogram';
import { TFunction } from '@tuic/i18n';
import { twJoin } from 'tailwind-merge';
import { useClientTranslation } from '@lib/i18n/client';

export enum ConnectionStatus {
  Unknown,
  NoConnection,
  Connected,
  Active,
  Paused,
  Exhausted,
}

type Props = {
  status: ConnectionStatus;
  size: 'sm' | 'md';
};

export const ConnectionStatusSymbol: FC<Props> = ({ status, size }) => {
  const { t } = useClientTranslation();

  const label = renderLabel(t, status);

  return (
    <div
      className={twJoin(
        'text-marine-high-emphasis',
        size === 'sm' && ['bg-white px-3 rounded-md', label ? 'py-1' : 'py-2'],
      )}
    >
      {size === 'sm' && (
        <Icon name={getIconName(status)} size="md" className="block mx-auto" />
      )}
      {size === 'md' && (
        <Pictogram
          name={getPictogramName(status)}
          size="md"
          className={twJoin('block mx-auto')}
        />
      )}

      {label && (
        <div
          className={twJoin(
            'flex items-center justify-center gap-1 font-semibold text-xs',
          )}
        >
          {label}
        </div>
      )}
    </div>
  );
};

const getPictogramName = (status: ConnectionStatus): PictogramName => {
  if (
    status === ConnectionStatus.Active ||
    status === ConnectionStatus.Paused
  ) {
    // OM-2602
    return 'wifi';
  }

  return 'no-wifi';
};

const getIconName = (status: ConnectionStatus): IconName => {
  if (status === ConnectionStatus.NoConnection) {
    return 'no-wifi';
  }

  return 'wifi';
};

const renderLabel = (t: TFunction, status: ConnectionStatus) => {
  if (status === ConnectionStatus.Active) {
    return (
      <>
        <div>{t('aktiv')}</div>
        <div className="size-2 bg-success-100 rounded-full" />
      </>
    );
  }

  if (status === ConnectionStatus.Paused) {
    return (
      <>
        <div>{t('pausiert')}</div>
        <div className="size-2 bg-error-100 rounded-full" />
      </>
    );
  }

  return null;
};
